/**
 * Tenant-aware string helpers.
 *
 * The Pages middleware (`functions/_middleware.ts`) injects
 * `window.__TENANT_CONFIG__` at HTML response time, including the tenant's
 * display name and branding fields. These helpers centralize the read paths
 * so every piece of user-facing text (page titles, install prompts, support
 * email, push notifications) stays in sync with the active tenant.
 *
 * For Cravo standalone deployments (no tenant config injected), every helper
 * falls back to the Cravo-branded defaults — preserving the current product
 * experience for that deployment.
 */

const DEFAULT_TENANT_NAME = 'Cravo'
const DEFAULT_SUPPORT_EMAIL = 'support@cravo.app'

/**
 * The tenant's display name (e.g. "Orange"). Falls back to "Cravo" when no
 * tenant config is present. Use this as the prefix for product titles —
 * `${tenantName()} Partner`, `${tenantName()} AI`, etc.
 */
export function tenantName(): string {
  const cfg = typeof window !== 'undefined' ? window.__TENANT_CONFIG__ : undefined
  return cfg?.branding?.name || cfg?.tenant?.name || DEFAULT_TENANT_NAME
}

/**
 * The full product name as users see it in headers, install prompts, page
 * titles, etc. Cravo deployment: "Cravo Partner". Orange tenant: "Orange Partner".
 */
export function partnerAppName(): string {
  return `${tenantName()} Partner`
}

/**
 * The AI feature's product name (sidebar entry, page header). Cravo: "Cravo AI".
 * Orange: "Orange AI".
 */
export function aiFeatureName(): string {
  return `${tenantName()} AI`
}

/**
 * Tenant-configured support email if the platform admin set one in the
 * branding config; otherwise the Cravo fallback. Use this for `mailto:`
 * links and contact CTAs so tenant customers reach the tenant's own support
 * channel, not Cravo's.
 */
export function supportEmail(): string {
  const cfg = typeof window !== 'undefined' ? window.__TENANT_CONFIG__ : undefined
  const branding = cfg?.branding as { support_email?: string } | undefined
  return branding?.support_email || DEFAULT_SUPPORT_EMAIL
}

/**
 * User-friendly notice shown in place of a map when Maps can't be loaded
 * because the tenant hasn't been given a Google Maps key yet. Kept
 * deliberately non-technical and actionable.
 */
export const MAPS_UNAVAILABLE_MESSAGE =
  "Map isn't available yet — the maps provider hasn't been set up for this store. Please ask your platform admin to configure it under Maps & Location, then refresh this page."

/**
 * The Google Maps JS API key to load with — FAIL CLOSED on LabelGenic.
 *
 * - LabelGenic tenant (tenant config injected) WITH its own key
 *   (`window.__TENANT_CONFIG__.maps.api_key`, set in the admin panel and
 *   surfaced by integrations-bootstrap.ts) → use the tenant's key, so Maps
 *   quota/billing/referrer-restrictions hit the tenant's own Google project.
 * - LabelGenic tenant WITHOUT a key → return '' (NO fallback). We must NOT
 *   silently fall back to the Cravo build-time key, or every unconfigured
 *   tenant's map usage would be billed to Cravo's Google project. Callers
 *   detect the empty key and show MAPS_UNAVAILABLE_MESSAGE instead.
 * - Cravo standalone deployment (no tenant config at all) → the build-time
 *   `VITE_GOOGLE_MAPS_KEY` IS Cravo's own key for Cravo's own usage, so
 *   that's the correct, intended key there.
 */
function nonEmpty(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null
}

/** A maps block is usable only if it is not explicitly disabled. The
 *  backend may keep a stale key value with `enabled:false` (baseline
 *  default) — using it would show a map for a tenant that never enabled
 *  Maps (and bill the wrong Google project). enabled true/absent = ok. */
function enabledOk(block?: { enabled?: boolean }): boolean {
  return !!block && block.enabled !== false
}

/**
 * Active map provider for this tenant. Read from
 * `window.__TENANT_CONFIG__.integrations.active_map_provider` (set in the
 * LabelGenic dashboard's Maps & Location page). Defaults to 'google' for
 * backward compat — every tenant that existed before this field landed
 * still has a configured google_maps integration.
 */
export type MapProviderId = 'google' | 'free_stack'

export function activeMapProvider(): MapProviderId {
  const cfg = typeof window !== 'undefined' ? window.__TENANT_CONFIG__ : undefined
  // `active_map_provider` is a scalar string nested alongside the per-
  // integration objects in `integrations_json`. It reaches the client in
  // two shapes:
  //   1. Inline `integrations.active_map_provider` (string) — R2/middleware,
  //      if the middleware ever inlines integrations.
  //   2. Top-level `active_map_provider` — written by integrations-bootstrap
  //      from GET /platform-config/integrations, where the backend wraps the
  //      scalar as `{ enabled, credentials: { value } }` and the bootstrap
  //      flattens it to `{ enabled, value }`.
  const ints = cfg?.integrations as Record<string, unknown> | undefined
  const inline = ints?.active_map_provider
  if (inline === 'free_stack') return 'free_stack'
  if (inline === 'google') return 'google'
  const top = (cfg as unknown as Record<string, unknown> | undefined)?.active_map_provider
  const v = typeof top === 'string' ? top : (top as { value?: unknown } | undefined)?.value
  return v === 'free_stack' ? 'free_stack' : 'google'
}

/**
 * Free Stack API keys (MapTiler / LocationIQ / OpenRouteService). All
 * three need to be present for the Free Stack provider to actually work
 * end-to-end; the Maps & Location dashboard surfaces partial readiness
 * (e.g. "2/3 ready") so the admin knows what's still missing.
 */
export interface FreeStackKeys {
  maptiler: string
  locationiq: string
  openrouteservice: string
}

export function freeStackKeys(): FreeStackKeys {
  const cfg = typeof window !== 'undefined' ? window.__TENANT_CONFIG__ : undefined
  const ints = cfg?.integrations as Record<string, any> | undefined
  // Blocks arrive in two shapes: inline `integrations.<provider>` (R2/
  // middleware, if ever inlined) and top-level `<provider>` written by
  // integrations-bootstrap (which flattens `{enabled, credentials}` from
  // GET /platform-config/integrations into `{enabled, ...credentials}`).
  const top = cfg as unknown as Record<string, any> | undefined
  const read = (provider: string): string => {
    const block = ints?.[provider] ?? top?.[provider]
    // Same enabled-gate as mapsApiKey(): a block the admin toggled OFF in
    // the dashboard may still carry a stale key — never use it.
    if (!enabledOk(block)) return ''
    return nonEmpty(block?._api_key ?? block?.api_key ?? block?.credentials?.api_key) ?? ''
  }
  return {
    maptiler:         read('maptiler'),
    locationiq:       read('locationiq'),
    openrouteservice: read('openrouteservice'),
  }
}

export function mapsApiKey(): string {
  const cfg = typeof window !== 'undefined' ? window.__TENANT_CONFIG__ : undefined
  if (cfg) {
    // LabelGenic deployment — only the tenant's own ENABLED key is
    // acceptable (fail closed; never the Cravo build key). Precedence:
    //   1. inline integrations.* (R2/middleware, pre-bootstrap)
    //   2. google_maps.web_key  (CURRENT — getPublicIntegrations + bootstrap)
    //   3. maps.api_key         (LEGACY)
    const gmInline = cfg.integrations?.google_maps
    if (enabledOk(gmInline)) {
      const k = nonEmpty(gmInline!.credentials?.web_key)
      if (k) return k
    }
    const mInline = cfg.integrations?.maps
    if (enabledOk(mInline)) {
      const k = nonEmpty(mInline!.credentials?.api_key)
      if (k) return k
    }
    if (enabledOk(cfg.google_maps)) {
      const k = nonEmpty(cfg.google_maps!.web_key)
      if (k) return k
    }
    if (enabledOk(cfg.maps)) {
      const k = nonEmpty(cfg.maps!.api_key)
      if (k) return k
    }
    return '' // fail closed
  }
  // Cravo standalone (no tenant config at all) — build-time key is Cravo's own.
  return import.meta.env.VITE_GOOGLE_MAPS_KEY || ''
}

/**
 * Three-tier logo fallback:
 *   1. The tenant's own logo (square preferred, then primary) — set in the
 *      LabelGenic admin's branding tab.
 *   2. The platform logo (LabelGenic) — used when the tenant hasn't uploaded
 *      one yet but we're on a LabelGenic deployment. Detected by the
 *      presence of any injected tenant config (the Cravo single-tenant
 *      deploy never injects this).
 *   3. The Cravo icon — the default for the Cravo standalone deployment.
 */
export function appLogoUrl(): string {
  const cfg = typeof window !== 'undefined' ? window.__TENANT_CONFIG__ : undefined
  const logos = cfg?.branding?.logos
  const tenantLogo = logos?.square || logos?.primary
  if (tenantLogo) return tenantLogo
  if (cfg) return '/labelgenic-icon.png'
  return '/icon.png'
}
